// packages/ui/src/lib/utils.ts
import * as React from "react";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * cn - Merge class names with conditional logic.
 * Uses clsx for conditional merging and tailwind-merge to resolve Tailwind conflicts.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * withRef - Create a forwardRef component with displayName and proper typing.
 * Simplified approach that works with React's forwardRef expectations.
 */
export function withRef<T, P = {}>(
  name: string,
  render: (props: P & React.RefAttributes<T>, ref: React.ForwardedRef<T>) => React.ReactElement | null
): React.ForwardRefExoticComponent<P & React.RefAttributes<T>> {
  const Component = React.forwardRef<T, P>((props, ref) => {
    return render(props as P & React.RefAttributes<T>, ref);
  });
  Component.displayName = name;
  return Component as React.ForwardRefExoticComponent<P & React.RefAttributes<T>>;
}

/**
 * mergeRefs - Combine multiple refs into a single callback ref.
 * Useful when a component needs both internal and forwarded refs.
 */
export function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | null | undefined>
): React.RefCallback<T> {
  return (value: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(value);
      } else {
        try {
          (ref as React.MutableRefObject<T>).current = value;
        } catch {
          // ignore readonly refs
        }
      }
    });
  };
}

/**
 * composeEventHandlers - Compose multiple event handlers into one.
 * If any handler calls event.preventDefault(), subsequent handlers are skipped.
 */
export function composeEventHandlers<E extends React.SyntheticEvent>(
  ...fns: Array<((event: E) => void) | null | undefined>
): (event: E) => void {
  return (event: E) => {
    for (const fn of fns) {
      if (!fn) continue;
      if (event.defaultPrevented) return;
      try {
        fn(event);
      } catch {
        // swallow errors - don't crash render
      }
    }
  };
}

/**
 * isBrowser - True only in browser environments (not SSR).
 */
export const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

/**
 * cssVar - Safely read a CSS custom property (with SSR support).
 */
export function cssVar(name: string, fallback = ""): string {
  if (!isBrowser) return fallback;
  try {
    const val = getComputedStyle(document.documentElement).getPropertyValue(name);
    return val ? val.trim() : fallback;
  } catch {
    return fallback;
  }
}

/**
 * noop - No-operation function for default props.
 */
export const noop = () => {};

/**
 * capitalize - Capitalize first letter of a string.
 */
export function capitalize(str?: string | null): string {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default cn;